import {
  Map,
  List,
  Range,
  Iterable as ImmutableIterable,
  Collection,
} from 'immutable';
import { getRandomInclusive } from 'app/utils/random';

export const isImmutable = (a: unknown) => ImmutableIterable.isIterable(a);

const getOrElse = <K, V>(c: Collection<K, V>, key: K, orElse: (c: Collection<K, V>, key: K) => V): V => (
  c.get(key) || orElse(c, key)
);

const getAtKeyOrElseThrow = <K, V>(c: Collection<K, V>, key: K, error?: Error) => getOrElse(c, key, () => {
  if (error) {
    throw error;
  } else {
    throw new Error(`${key} undefined`);
  }
});

export const selectKeys = <K, V>(m: Map<K, V>, ks: Iterable<K>) => {
  const selected = Map<K, V>().asMutable();
  for (const k of ks) {
    if (m.has(k)) {
      selected.set(k, getAtKeyOrElseThrow(m, k));
    }
  }
  return selected.asImmutable();
};

export const indexBy = <V, I>(xs: Iterable<V>, f: (x: V) => I) => {
  const m = Map<I, V>().asMutable();
  for (const x of xs) {
    m.set(f(x), x);
  }
  return m.asImmutable();
};

// directly from immutable.js, which doesn't expose a higher
// level comparator type
export const defaultComparator = <T>(a: T, b: T) => (
  a > b ? 1 : a < b ? -1 : 0
);

export const reverseComparator = <T>(comparator: (a: T, b: T) => number) => (a: T, b: T) =>
  -comparator(a, b);

export const chunk = <T>(list: List<T>, chunkSize = 1) =>
  Range(0, list.count(), chunkSize).map(chunkStart =>
    list.slice(chunkStart, chunkStart + chunkSize));

/**
 * Given a list and two indicies in the list, return a list with the values swapped
 * @param list the list to swap in
 * @param i the first index
 * @param j the second index
 * @returns a list with the values at i and j in the original list swapped.
 */
export const swap = <T>(list: List<T>, i: number, j: number) => {
  const originalI = getAtIndexOrElseThrow(list, i);
  const originalJ = getAtIndexOrElseThrow(list, j);

  return list.set(i, originalJ).set(j, originalI);
};

/**
 * Shuffles an Immutable List using Fisher-Yates. Takes an optional random function that should
 * return a value between [0,1) like Math.random, intended for using a seeded RNG.
 */
export const shuffle = <T>(
  list: List<T>,
  randomFunction = Math.random,
) => list.withMutations(ids => {
  for (let i = 0; i < ids.size - 1; i++) {
    const swapIndex = getRandomInclusive(i, ids.size - 1, randomFunction());
    const currentItem = ids.get(i);
    const swapItem = ids.get(swapIndex);

    // Asserting these will not be undefined (unless T extends undefined in which case it is still safe)
    ids.set(i, swapItem!);
    ids.set(swapIndex, currentItem!);
  }
});

/**
 * Takes an Immutable.js List and a function that maps the elements of the list to some comparable
 * value, and returns a List of Lists, where each sublist contains adjacent values that got mapped
 * to the same comparable value (using referential equality).
 *
 * For example:
 *
 * `groupAdjacent(List([1, 2, 2], x => x))`
 *
 * returns:
 *
 * `List([List([1]), List([2, 2])])`
 *
 * and:
 *
 * `groupAdjacent(List(['foo', 'bar', 'foobar', 'baz'], s => s.length))`
 *
 * returns:
 *
 * `List([List(['foor', 'bar']), List(['foobar']), List(['baz'])])`
 *
 * because 'foo' and 'bar' are adjacent and are the same length.
 *
 *
 * A custom equality checker function can be passed as a third argument (defaulting to referential
 * equality). For each pair of adjacent values in the list, the given function will be passed:
 *
 * ```
 * (
 *   prev, // The left value of the pair
 *   next, // The right value of the pair
 *   {
 *     currentGroup,   // The List of values for the current group so far
 *     previousGroups, // A List of all completed group Lists so far
 *     index,          // The index of the right value of the pair in the original list
 *   }
 * )
 * ```
 *
 * If the equality function returns true, then the next value will be added to the current group. If
 * the value returns false, a new group will be started.
 */

export interface IContext<T> {
  currentGroup: List<T>,
  previousGroups: List<List<T>>,
  index: number,
}

export function groupAdjacent<Input, Mapped>(
  list: List<Input>,
  mapToComparableValue: ((x: Input, index: number) => Mapped),
  isEqual = ((a: Mapped, b: Mapped, context: IContext<Input>) => a === b),
): List<List<Input>> {
  if (list.size === 0) {
    return List();
  }

  let groups = List<List<Input>>().asMutable();
  // Asserting that the list has a first value since we checked the size before this.
  let currentGroup: List<Input> = List([list.first()!]);

  for (let i = 1; i < list.size; i++) {
    // These values will definitely be defined unless T extends undefined. Either way
    // this is safe.
    const prev: Input = list.get(i - 1)!;
    const next: Input = list.get(i)!;

    const adjacentEqual = isEqual(
      mapToComparableValue(prev, i - 1),
      mapToComparableValue(next, i),
      {
        currentGroup,
        previousGroups: groups.asImmutable(),
        index: i,
      },
    );

    if (adjacentEqual) {
      // Continue the current group.
      currentGroup = currentGroup.push(next);
    } else {
      // Start a new group.
      groups = groups.asMutable();
      groups.push(currentGroup);
      currentGroup = List([next]);
    }
  }

  groups = groups.asMutable();
  groups.push(currentGroup);

  return groups.asImmutable();
}

/**
 * Useful to convert arrays to immutable lists because they allow
 * generators. It has the advantage that an intermediary array is not
 * generated For example:
 *
 * ```
 * const array = [{ name: 1 }, { name: 2 }, ...];
 * const list = List(yieldForEach(array, ({ name }) => name));
 * ```
 *
 * @param {Array} array
 * @param {Function} fn - returns yielded value, called with `(element,
 * index, array)`
 */
export const yieldForEach = function*<T, R>(array: T[], fn: (t: T, i: number, whole: T[]) => R) {
  for (let i = 0; i < array.length; ++i) {
    yield fn(array[i], i, array);
  }
};

/**
 * Finds duplicates in a list. This uses standard javascript equality.
 *
 * @param {List} list
 */
export const findDuplicates = <T>(list: List<T>) => {
  return list
    .groupBy((value) => value)
    .filter((group) => group.size > 1)
    .map((group, value) => value);
};

export const removeDuplicatesBy = <T>(
  list: List<T>,
  mapToUniqueValue: ((value: T, index: number) => unknown) = (x => x),
): List<T> => {
  const uniqueValueSet = new Set<unknown>();

  return list.reduce((result, value, index) => {
    const uniqueValue = mapToUniqueValue(value, index);
    if (uniqueValueSet.has(uniqueValue)) {
      return result;
    } else {
      uniqueValueSet.add(uniqueValue);
      return result.push(value);
    }
  }, List());
};

/**
 * Filters undefined elements out of a list.
 *
 * @param {List} l
 */
export const filterUndefined = <T>(list: List<T | undefined>): List<T> => (
  filterType<T, undefined>(list, (x: any): x is undefined => x === undefined)
);

/**
 * Filter an array to remove a given type.
 *
 * @param list A list containing both Good and Evil values (Evil being the values we want to remove)
 * @param isEvil A predicate to tell us which values are evil.
 */
export const filterType = <Good, Evil>(list: List<Good | Evil>, isEvil: (x: any) => x is Evil) => {
  const filtered = List<Good>().asMutable();
  list.forEach(x => {
    if (!isEvil(x)) {
      filtered.push(x);
    }
  });
  return filtered.asImmutable();
};

/**
 * Validate that a given index is within the range of a given list
 * @params i the index to validate
 * @params list the list of validate
 */
export const validateIndex = <T>(i: number, list: List<T>) => {
  if (i < 0 || i >= list.size) {
    throw new Error(`index ${i} out of bounds for list of length ${list.size}`);
  }
};

/**
 * Given a list and an index, get the value at the index or throw an error.
 * @param list A list potentially containing a value at the given index.
 * @param index The index to check for a value
 * @throws an exception if the index is not valid for the given list
 * @returns The value in the list at the given index.
 */
export const getAtIndexOrElseThrow = <T>(list: List<T>, index: number): T => {
  const maybeValue = list.get(index);
  if (maybeValue === undefined) {
    throw new Error(`The list did not have a value at position ${index}`);
  }
  return maybeValue;
};
